/**
 * Durable (localStorage-backed) breadcrumb for boot recovery actions that
 * survive a page reload.
 *
 * Some recovery steps end in an immediate `window.location.reload()` — the boot
 * watchdog's Reload / Reset buttons, and the `versionchange` stale-tab reload in
 * `db.ts`. A telemetry event captured right before that reload would race the
 * navigation and usually be lost. Instead we drop a small breadcrumb here; the
 * NEXT boot reads it and can:
 *   1. drive the escalation ladder — if a plain reload already happened and the
 *      boot is STILL stuck, escalate straight to a local-data reset instead of
 *      asking the user to reload again into the same wedge; and
 *   2. emit accurate telemetry — `boot.recovered` (recovery worked) or a repeat
 *      stall — once the sink is initialised and can actually flush.
 *
 * localStorage (not IndexedDB) on purpose: it's synchronous, survives the
 * reload, and is exactly the store the reset flow deliberately preserves (the
 * bearer token lives there too), so a breadcrumb can never be wiped by the very
 * reset it's meant to describe.
 */

const KEY = "stockline:boot:recovery"

export type BootRecoveryReason =
  /** User pressed Reload in the recovery panel. */
  | "reload"
  /** User pressed "Reset local data & reload" in the recovery panel. */
  | "reset"
  /** A stale tab auto-reloaded to pick up a newer schema/bundle (multi-tab deploy). */
  | "stale-upgrade"
  /** The watchdog auto-ran a reset after a reload still landed stuck. */
  | "auto-reset"

export interface BootRecoveryMark {
  reason: BootRecoveryReason
  at: number
}

const VALID_REASONS: ReadonlySet<string> = new Set<BootRecoveryReason>([
  "reload",
  "reset",
  "stale-upgrade",
  "auto-reset",
])

export function markBootRecovery(reason: BootRecoveryReason): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(
      KEY,
      JSON.stringify({ reason, at: Date.now() } satisfies BootRecoveryMark)
    )
  } catch {
    // localStorage can throw (private mode / quota) — the breadcrumb is a
    // best-effort optimisation, never load-bearing.
  }
}

export function readBootRecovery(): BootRecoveryMark | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<BootRecoveryMark>
    // Validate strictly: a malformed but JSON-parseable value must not pollute
    // recovery telemetry (bogus `reason`) or, worse, force the next stalled boot
    // into an auto-reset via a `reload` breadcrumb carrying a future / NaN /
    // Infinity timestamp. A future `at` is rejected too: it would otherwise
    // slip past the consumer's recency window (`now - at < RECOVERY_RECENT_MS`)
    // indefinitely, turning a forged or clock-skewed breadcrumb into a
    // perpetual auto-reset trigger.
    if (
      typeof parsed?.reason !== "string" ||
      !VALID_REASONS.has(parsed.reason) ||
      typeof parsed?.at !== "number" ||
      !Number.isFinite(parsed.at) ||
      parsed.at < 0 ||
      parsed.at > Date.now()
    ) {
      return null
    }
    return { reason: parsed.reason as BootRecoveryReason, at: parsed.at }
  } catch {
    return null
  }
}

export function clearBootRecovery(): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.removeItem(KEY)
  } catch {
    // ignore — see markBootRecovery.
  }
}
